import { FieldProps, FormControl, useFormField } from '@core/utils/form';
import { R } from '@core/utils/r';
import React, { CSSProperties, useCallback, useMemo } from 'react';

export interface FormUploadBaseProps<T extends File | any | File[] | any[]> {
  onChange?(value: T | null): void;
  style?: CSSProperties;
  accept?: string;
  image?: boolean;
}

export interface FormUploadSingleProps<T extends File | any> extends FormUploadBaseProps<T>, FieldProps<FormControl<T>> {
  type?: 'single';
}
export interface FormUploadMultipleProps<T extends File[] | any[]> extends FormUploadBaseProps<T>, FieldProps<FormControl<T>> {
  type?: 'multiple';
}

export type FormUploadProps<T extends any> = FormUploadSingleProps<T> | FormUploadMultipleProps<T>;

// export type FormUploadProps = FieldProps<FormControl<string>>;

export function FormUpload<T extends any>(props: FormUploadProps<T>) {
  const { formControl, fieldState, fieldConfig } = useFormField(props);

  const fieldValue = fieldState.value || null;

  const fieldReadonly = fieldConfig.readonly || null;
  const fieldDisabled = fieldConfig.disabled || null;

  const onChange = useCallback(
    (files: FileList | null) => {
      if (!files) return null;
      if (props.type === 'multiple') {
        const data: File[] = [];
        for (let i = 0; i < files.length; i++) {
          data.push(files[i]);
        }

        formControl.setValue(data as any);
      } else {
        formControl.setValue(files[0] as any);
      }
    },
    [formControl, props.type],
  );

  const label = useMemo(() => {
    if (fieldValue) {
      if (props.type === 'multiple') {
        return (fieldValue as File[]).map(f => f.name).join(', ');
      } else {
        return (fieldValue as File).name;
      }
    } else {
      return fieldConfig.label;
    }
  }, [fieldConfig.label, fieldValue, props.type]);

  return (
    <React.Fragment>
      <div>
        <div className="custom-file mb-3">
          <input
            accept={props.accept || (props.image ? 'image/*' : undefined)}
            type="file"
            multiple={props.type === 'multiple'}
            className="custom-file-input"
            onChange={e => onChange(e.target.files)}
          />
          <label className="custom-file-label" htmlFor="customFile">
            {label}
          </label>
        </div>
        {R.isString(fieldValue) && props.image && <img src={fieldValue} style={{ width: 100, marginBottom: 20 }} />}
      </div>
    </React.Fragment>
  );
}
